import React, { useEffect, useState } from "react";
import { CalendarCheck } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { api, waLink } from "../lib/constants";
import { Reveal } from "../components/Reveal";
import { Overline, SectionTitle, GoldButton, PageHero } from "../components/site/Primitives";
import SEO from "../components/SEO";

export default function Barbearia() {
  const { site } = useSite();
  const [services, setServices] = useState([]);
  const img = site?.images || {};
  const professionals = site?.professionals || [];
  const teamPhoto = site?.team_photo;

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
  }, []);

  return (
    <div>
      <SEO title="Barbearia em Arapiraca | Barbearia das Estrelas" description="Cortes modernos, sociais, barba e estética masculina na Barbearia das Estrelas em Arapiraca. Agende seu horário." />
      <PageHero
        overline="Barbearia das Estrelas"
        title="SEU ESTILO COMEÇA AQUI"
        subtitle="Na Barbearia das Estrelas, cada atendimento é pensado para valorizar o estilo de cada cliente. Nossa equipe trabalha com cortes modernos, cortes sociais, barba, estética masculina e diversos serviços."
        image={img.barbershop_hero}
      >
        <GoldButton href={waLink(site, "barbearia")} size="lg" data-testid="barbearia-hero-agendar"><CalendarCheck className="w-5 h-5" /> AGENDAR MEU HORÁRIO</GoldButton>
      </PageHero>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
        <Reveal><Overline className="mb-3">Serviços</Overline></Reveal>
        <Reveal delay={0.05}><SectionTitle>O que fazemos por você</SectionTitle></Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-10">
          {services.map((s, i) => (
            <Reveal key={s.id} delay={(i % 4) * 0.05}>
              <div className="group relative aspect-[4/5] overflow-hidden rounded-sm border border-white/10">
                <img src={s.image} alt={s.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="font-heading font-600 uppercase text-white text-sm tracking-wide">{s.name}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TEAM */}
      {(teamPhoto || professionals.length > 0) && (
        <section className="bg-[#080808] border-y border-white/10 py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <Reveal>
                <div>
                  <Overline className="mb-3">Nossa equipe</Overline>
                  <SectionTitle>Profissionais especializados</SectionTitle>
                  <p className="mt-4 text-zinc-400 leading-relaxed max-w-md">
                    Uma equipe dedicada a valorizar o estilo de cada cliente — cortes modernos, sociais, barba e estética masculina do início ao fim do atendimento.
                  </p>
                  <div className="mt-7">
                    <GoldButton href={waLink(site, "barbearia")} data-testid="barbearia-team-agendar"><CalendarCheck className="w-4 h-4" /> AGENDAR MEU HORÁRIO</GoldButton>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                {teamPhoto ? (
                  <div className="relative overflow-hidden rounded-sm border border-white/12 aspect-[4/5] lg:aspect-[3/4]" data-testid="team-photo">
                    <img src={teamPhoto} alt="Equipe da Barbearia das Estrelas em atendimento" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent pointer-events-none" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {professionals.map((p, i) => (
                      <div key={p.id} className="group relative aspect-[3/4] overflow-hidden rounded-sm border border-white/10">
                        <img src={p.image} alt={p.name || p.role} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          {p.name ? <p className="font-heading font-600 text-white text-base leading-tight">{p.name}</p> : null}
                          <span className="overline-label text-[10px] text-gold">{p.role}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24 text-center">
        <Reveal><SectionTitle className="max-w-2xl mx-auto">Pronto para renovar o visual?</SectionTitle></Reveal>
        <Reveal delay={0.1}>
          <div className="mt-8 flex justify-center">
            <GoldButton href={waLink(site, "barbearia")} size="lg" data-testid="barbearia-cta-agendar"><CalendarCheck className="w-5 h-5" /> AGENDAR MEU HORÁRIO</GoldButton>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
